import { useNavigate } from "react-router-dom";
import { ArrowLeft, Truck, Flag, Forklift, PackageCheck, Container, PackageSearch } from "lucide-react";
import { useTranslation } from "../i18n";
import { usePageTitle } from "../hooks/usePageTitle";
import { isVisible, useHomeModuleVisibility, readUserRole } from "../components/helpers/homeModuleVisibility";
import { useBadgeCounts } from "../hooks/useBadgeCounts";
import "../styles/home.css";

const DispatchMenu = () => {
    usePageTitle("dispatch");
    const navigate = useNavigate();
    const { t } = useTranslation();

    const userData = window.localStorage.getItem("user");
    const userObject = userData !== null ? JSON.parse(userData) : { warehouse: "" };
    const role = readUserRole();
    const moduleMap = useHomeModuleVisibility(userObject.warehouse);
    const show = (key: string) => isVisible(moduleMap, key, role);

    // Ready totes/items + flagged-order count come precomputed from the
    // server (GET /badges), the same figures the homepage Dispatch tile shows.
    const counts = useBadgeCounts(userObject.warehouse);
    const showStart = show("dispatch.start");
    // Pallet desk (2026-10-30): its own module key, apart from pack and ship.
    const showPalletDispatch = show("dispatch.pallets");
    const readyToteCount = counts.dispatch.count;
    const readyItemCount = counts.dispatch.items;
    const showFlagged = show("dispatch.flagged");
    const flaggedCount = counts.flaggedOrders.count;
    // Conveyor unload (2026-10-14): parcel-to-courier-pallet validation at
    // the end of the pack-desk conveyor. Its own module key so the
    // unloader need not be a packer.
    const showUnload = show("dispatch.unload");
    // Load lorry (2026-10-19): closed pallets scanned onto the vehicle.
    const showLoad = show("dispatch.load");
    // Found items (2026-10-20): a found unit onto the shelf for its order.
    const showFound = show("dispatch.found_items");

    return (
        <div className="page-data" style={{ height: "calc(100vh - 56px)", overflow: "auto" }}>
            <div className="page-title-bar">
                <h1 className="page-title">{t("dispatch")}</h1>
                <button className="page-back-btn" onClick={() => navigate("/")}>
                    <ArrowLeft size={20} />
                </button>
            </div>
            <div className="home-grid">
                {showStart && (
                    <button className="home-card home-card-purple" onClick={() => navigate("/dispatch")}>
                        {(readyItemCount > 0 || readyToteCount > 0) && (
                            <div className="home-card-badge-group">
                                {readyItemCount > 0 && (
                                    <span className="home-card-badge" aria-label={`${readyItemCount} items`}>
                                        {readyItemCount}
                                    </span>
                                )}
                                {readyToteCount > 0 && (
                                    <span className="home-card-badge home-card-badge--totes" aria-label={`${readyToteCount} totes`}>
                                        {readyToteCount}
                                    </span>
                                )}
                            </div>
                        )}
                        <div className="home-card-icon"><Truck size={48} strokeWidth={1.5} /></div>
                        <div className="home-card-title">{t("dispatch_start")}</div>
                    </button>
                )}
                {/* Pallet desk — pallet orders booked with Palletways or shipped
                    by a manual pallet carrier. Its own module key. */}
                {showPalletDispatch && (
                    <button className="home-card home-card-purple" onClick={() => navigate("/dispatch/pallets")}>
                        <div className="home-card-icon"><Forklift size={48} strokeWidth={1.5} /></div>
                        <div className="home-card-title">{t("pallet_dispatch")}</div>
                    </button>
                )}
                {showUnload && (
                    <button className="home-card home-card-purple" onClick={() => navigate("/dispatch/unload")}>
                        <div className="home-card-icon"><PackageCheck size={48} strokeWidth={1.5} /></div>
                        <div className="home-card-title">{t("dispatch_unload")}</div>
                    </button>
                )}
                {showLoad && (
                    <button className="home-card home-card-purple" onClick={() => navigate("/dispatch/load")}>
                        <div className="home-card-icon"><Container size={48} strokeWidth={1.5} /></div>
                        <div className="home-card-title">{t("dispatch_load")}</div>
                    </button>
                )}
                {showFound && (
                    <button className="home-card home-card-purple" onClick={() => navigate("/dispatch/found-items")}>
                        <div className="home-card-icon"><PackageSearch size={48} strokeWidth={1.5} /></div>
                        <div className="home-card-title">{t("dispatch_found_items")}</div>
                    </button>
                )}
                {showFlagged && (
                    <button className="home-card home-card-purple" onClick={() => navigate("/dispatch/flagged")}>
                        {flaggedCount > 0 && (
                            <div className="home-card-badge-group">
                                <span className="home-card-badge" aria-label={`${flaggedCount} flagged orders`}>
                                    {flaggedCount}
                                </span>
                            </div>
                        )}
                        <div className="home-card-icon"><Flag size={48} strokeWidth={1.5} /></div>
                        <div className="home-card-title">{t("dispatch_flagged")}</div>
                    </button>
                )}
            </div>
        </div>
    );
};

export default DispatchMenu;
